import type { Regime } from './types.js';

/**
 * 방문자 국가로 적용 규제를 정한다.
 *
 * **현행 `partner` 설정을 기준으로 삼았다** — `gdprAppliesGlobally: false`,
 * `lgpdAppliesGlobally: false`, `enableCcpa`, `enableLgpd`
 * (PL 결정 2026-10-07).
 *
 * 즉 **규제가 요구하는 곳에만 띄운다.** 그 밖에서는 `none` 이고 배너가
 * 뜨지 않는다. 종전 `homepage` 는 전 세계에 띄우고 있었으므로 그쪽은
 * 범위가 줄어든다 — 과하게 적용하던 것을 표준으로 되돌리는 것이다.
 */

/** EEA 30개국 — EU 27 + 아이슬란드·리히텐슈타인·노르웨이. */
const EEA = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
]);

/**
 * 영국은 EEA 가 아니지만 **UK GDPR** 이 따로 있다. 탈퇴 후에도 사실상 같은
 * 요구라 GDPR 로 묶는다. 빠뜨리기 쉬운 자리다.
 */
const UK = 'GB';

/**
 * 규제를 판정한다.
 *
 * `country` 는 ISO 3166-1 alpha-2 다. **모르면 `null` 을 넘긴다.**
 *
 * @example
 *   regimeFor('DE')   // 'gdpr'
 *   regimeFor('US')   // 'ccpa'
 *   regimeFor('JP')   // 'none'  — 배너를 띄우지 않는다
 *   regimeFor(null)   // 'gdpr'  — 모르면 가장 엄격하게
 */
export function regimeFor(country: string | null | undefined): Regime {
  if (country === null || country === undefined || country === '') {
    // **모르면 가장 엄격한 쪽으로 간다.** 판별에 실패했다고 배너를 끄면,
    // 판별이 고장난 순간 모든 방문자에게 동의 없이 쿠키를 심게 된다.
    // 틀렸을 때의 대가가 비대칭이다.
    return 'gdpr';
  }
  const c = country.trim().toUpperCase();
  if (EEA.has(c) || c === UK) return 'gdpr';
  if (c === 'US') return 'ccpa';
  if (c === 'BR') return 'lgpd';
  return 'none';
}

/**
 * 그 규제에서 **동의 전에 쿠키를 심어도 되는가**.
 *
 * GDPR·LGPD 는 **옵트인**이다 — 동의 전에는 안 된다.
 * CCPA 는 **옵트아웃**이다 — 거부하기 전까지는 허용되며, 배너는 거부할
 * 수단을 제공하는 역할이다.
 */
export function needsPriorConsent(regime: Regime): boolean {
  return regime === 'gdpr' || regime === 'lgpd';
}

/** 그 규제에서 배너를 띄우는가. */
export function showsBanner(regime: Regime): boolean {
  return regime !== 'none';
}
